import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader, AlertCircle, FolderPlus } from 'lucide-react';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { buildTeamApiService } from '../../services/buildTeamApi';
import {
  BuildProjectStatus,
  BuildTag,
  ProjectCategory,
  BUILD_PROJECT_STATUSES,
  PROJECT_CATEGORIES,
} from '../../types/buildTeam';
import { cn } from '../../utils/cn';
import { ApogeePackSearchField } from '../apogee/ApogeePackSearchField';

export interface CreateProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

function formatStatusLabel(s: string): string {
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

const LABEL_CLASS = 'text-xs font-medium text-glass-400 mb-1.5 block';

export function CreateProjectModal({ isOpen, onClose, onSuccess }: CreateProjectModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(modalRef, onClose);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<BuildProjectStatus>('planning');
  const [priority, setPriority] = useState(5);
  const [category, setCategory] = useState<ProjectCategory | ''>('');
  const [dueDateLocal, setDueDateLocal] = useState('');
  const [modpackApogeeId, setModpackApogeeId] = useState<number | null>(null);
  const [tags, setTags] = useState<BuildTag[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [isLoadingTags, setIsLoadingTags] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setTitle('');
    setDescription('');
    setStatus('planning');
    setPriority(5);
    setCategory('');
    setDueDateLocal('');
    setModpackApogeeId(null);
    setSelectedTagIds([]);
    setError(null);
    setIsLoadingTags(true);
    buildTeamApiService
      .listTags()
      .then(setTags)
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Failed to load tags');
      })
      .finally(() => setIsLoadingTags(false));
  }, [isOpen]);

  const handleToggleTag = (tagId: string) => {
    setSelectedTagIds((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    if (!description.trim()) {
      setError('Description is required');
      return;
    }

    setIsSubmitting(true);
    try {
      await buildTeamApiService.createProject({
        title: title.trim(),
        description: description.trim(),
        status,
        priority,
        category: category || undefined,
        apogee_id: modpackApogeeId ?? undefined,
        due_date: dueDateLocal.trim()
          ? Math.floor(new Date(dueDateLocal).getTime() / 1000)
          : undefined,
        tag_ids: selectedTagIds.length > 0 ? selectedTagIds : undefined,
      });
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create project');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="modal-overlay z-[9999]">
      <div
        ref={modalRef}
        className="modal-panel max-w-lg"
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-project-title"
      >
        <div className="modal-header">
          <h2 id="create-project-title" className="modal-title flex items-center gap-2">
            <FolderPlus className="w-5 h-5 text-accent-300" />
            New project
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="modal-close touch-manipulation"
            aria-label="Close"
            title="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {error && (
          <div className="alert alert-danger mb-4">
            <AlertCircle />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className={LABEL_CLASS}>Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="glass-input w-full"
              placeholder="Project title"
              required
            />
          </div>

          <div>
            <label className={LABEL_CLASS}>Description *</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="glass-input w-full"
              placeholder="What is this project about?"
              rows={4}
              required
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={LABEL_CLASS}>Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as BuildProjectStatus)}
                className="glass-select w-full"
              >
                {BUILD_PROJECT_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {formatStatusLabel(s)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={LABEL_CLASS}>Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as ProjectCategory | '')}
                className="glass-select w-full"
              >
                <option value="">None</option>
                {PROJECT_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {formatStatusLabel(c)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className={LABEL_CLASS}>Priority (1–10)</label>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min={1}
                max={10}
                value={priority}
                onChange={(e) => setPriority(Number(e.target.value))}
                className="flex-1 h-2 rounded-full appearance-none bg-glass-200/30 accent-accent-500"
              />
              <input
                type="number"
                min={1}
                max={10}
                value={priority}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  if (v >= 1 && v <= 10) setPriority(v);
                }}
                className="glass-input w-16 text-center tabular-nums"
              />
            </div>
          </div>

          <div>
            <label className={LABEL_CLASS}>Due date</label>
            <input
              type="datetime-local"
              value={dueDateLocal}
              onChange={(e) => setDueDateLocal(e.target.value)}
              className="glass-input w-full"
            />
          </div>

          <ApogeePackSearchField
            value={modpackApogeeId}
            onChange={setModpackApogeeId}
            label="Linked modpack (optional)"
            compact
            description="Search Apogee (GET /v1/search/packs) and link this project to the primary catalog pack when relevant."
            disabled={isSubmitting}
          />

          <div>
            <label className={LABEL_CLASS}>Tags</label>
            {isLoadingTags ? (
              <div className="p-3 flex items-center justify-center">
                <Loader className="w-4 h-4 animate-spin text-accent-400" />
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {tags.map((tag) => (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => handleToggleTag(tag.id)}
                    className={cn(
                      'chip transition-colors',
                      selectedTagIds.includes(tag.id)
                        ? 'text-white'
                        : 'hover:text-white hover:border-glass-200/25'
                    )}
                    aria-pressed={selectedTagIds.includes(tag.id)}
                    style={
                      selectedTagIds.includes(tag.id)
                        ? { borderColor: tag.color, backgroundColor: `${tag.color}20` }
                        : undefined
                    }
                  >
                    {tag.name}
                  </button>
                ))}
                {tags.length === 0 && (
                  <span className="text-xs text-glass-400">No tags available</span>
                )}
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              onClick={onClose}
              className="glass-button-secondary"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? <Loader className="w-4 h-4 animate-spin" /> : null}
              Create project
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
